import React, { useEffect } from 'react';
import cn from 'classnames';
import { Section, SettingsSectionBox, SettingsSectionLabel } from '@views/Settings/components';
import styles from './CardDav.module.scss';
import { useTextCopiedTimer } from '@repo/hooks';
import { useDispatch, useSelector } from 'react-redux';
import { selectCardDavIntegration } from '@repo/redux/reducers/user/integrations';
import { createCardDavLogin, fetchCardDavIntegration } from '@repo/redux/actions/integrations';
import StateBasedFadeTransition from '@components/transitions/StateBasedFadeTransition';
import { ActionCopyIcon, CheckIcon, LoaderIcon, PlusIcon } from '@repo/icons';
import { Overlay, Tooltip } from '@repo/components/tooltips';

type CardDavIntegrationState = 'default' | 'enabling' | 'enabled';
type CardDavLoginState = 'default' | 'creating' | 'created';

export function CopyBtn({
  label,
  value,
  className,
  copiedClassName,
}: {
  label: string;
  value: string;
  className?: string;
  copiedClassName?: string;
}) {
  const [valueCopied, copyValue] = useTextCopiedTimer(value, (e) => e.preventDefault(), 1600);

  return (
    <div className={cn(styles.copyBtn, className, valueCopied && copiedClassName)}>
      <StateBasedFadeTransition
        state={String(!!valueCopied)}
        elementByStateMap={{
          false: <span></span>,
          true: <span className="information">copied!</span>,
        }}
      />
      <Tooltip placement="left" overlay={valueCopied ? <></> : <Overlay>Copy {label}</Overlay>}>
        <button
          className={cn(styles.copyLinkBtn, valueCopied && styles.copied)}
          onClick={copyValue}
          disabled={valueCopied}
        >
          <StateBasedFadeTransition
            state={String(!!valueCopied)}
            elementByStateMap={{
              false: (
                <span>
                  <ActionCopyIcon color="textSecondary" />
                </span>
              ),
              true: (
                <span className={styles.checkIcon}>
                  <CheckIcon color="textSecondary" width="12" height="12" />
                </span>
              ),
            }}
          />
        </button>
      </Tooltip>
    </div>
  );
}

const ConnectedCardDav = () => {
  const dispatch = useDispatch();
  const cardDavIntegration = useSelector(selectCardDavIntegration);
  const username = `${cardDavIntegration.username}@sync.clay.earth`;

  let state: CardDavLoginState = 'default';
  if (cardDavIntegration && cardDavIntegration.gettingPassword) {
    state = 'creating';
  } else if (cardDavIntegration.password) {
    state = 'created';
  }

  return (
    <>
      <div className={styles.row}>
        {username}
        <CopyBtn value={username} label="username" />
      </div>
      <StateBasedFadeTransition
        state={state}
        elementByStateMap={{
          default: (
            <div
              className={cn(styles.row, styles.clickableRow)}
              onClick={() => dispatch(createCardDavLogin())}
            >
              Generate Password
              <PlusIcon width="12" height="12" color="primary" />
            </div>
          ),
          creating: (
            <div className={styles.row}>
              Generating Password
              <LoaderIcon width="12" height="12" />
            </div>
          ),
          created: (
            <div className={styles.row}>
              {cardDavIntegration.password}
              <CopyBtn value={cardDavIntegration.password} label="password" />
            </div>
          ),
        }}
      />
    </>
  );
};

export default function CardDav() {
  const dispatch = useDispatch();
  const cardDavIntegration = useSelector(selectCardDavIntegration);

  let state: CardDavIntegrationState = 'default';
  if (cardDavIntegration && cardDavIntegration.isFetching) {
    state = 'enabling';
  } else if (cardDavIntegration) {
    state = 'enabled';
  }

  useEffect(() => {
    if (!cardDavIntegration) {
      dispatch(fetchCardDavIntegration());
    }
  }, []);

  return (
    <Section>
      {!cardDavIntegration || state === 'enabling' ? (
        <LoaderIcon />
      ) : (
        <>
          <SettingsSectionLabel>Credentials</SettingsSectionLabel>
          <SettingsSectionBox className={styles.container}>
            {state === 'enabled' && <ConnectedCardDav />}
          </SettingsSectionBox>
          <div className={styles.infoText}>
            Connect Clay with your native contacts app by following{' '}
            <a href="https://go.clay.earth/address-book" target="_blank">
              these instructions
            </a>
            .
          </div>
        </>
      )}
    </Section>
  );
}
